define('home/collections/FeedItemCollection',[
    'underscore',

    'home/collections/common/ExtensibleCollection',

    'home/models/ActivityFeedItem',
    'home/models/UnreadCounter',
    'home/ActivityFactory',
    'home/activityExpander',

    'core/api',
], function (
    _,

    ExtensibleCollection,

    ActivityFeedItem,
    UnreadCounter,
    ActivityFactory,
    activityExpander,

    api
) {
    'use strict';

    var TIMELINE_ACTIVITIES_PATHNAME = 'timelines/activities';

    var parent = ExtensibleCollection;
    var parentProto = parent.prototype;


    var FeedItemCollection = parent.extend({
        url: api.getURL(TIMELINE_ACTIVITIES_PATHNAME),

        initialize: function (models, options) {
            parentProto.initialize.call(this, models, options);
            options = options || {};

            // Activity timelines are made distinct by these three parameters
            // e.g.,
            // Home feed : {type: 'home'}
            // Recent channel comments : {type: 'profile', index: 'user_created_threads', target: 'channel'}
            this.uniqueTogether = {
                type: options.type,
                index: options.index,
                target: options.target,
            };
            this.unreadCounter = new UnreadCounter(null, _.extend({
                prepareFetchOptions: this.prepareFetchOptions,
            }, this.uniqueTogether));
        },

        prepareFetchOptions: function (options) {
            options = options ? _.clone(options) : {};

            // Ensure that the parameters that distinguish the feed are serialized
            // into the URL. (Undefined values will simply be omitted by jQuery.)
            options.data = _.extend({}, this.uniqueTogether, options.data);

            return options;
        },

        fetch: function (options) {
            options = this.prepareFetchOptions(options);
            return parentProto.fetch.call(this, options);
        },

        canUpdate: function () {
            return (
                // only update non-empty collection
                this.length &&
                // right now we only know how to update collections backed by
                // 'timelines/activities' endpoint, not 'timelines/ranked' or others
                this.url.indexOf(TIMELINE_ACTIVITIES_PATHNAME) > 0
            );
        },

        updateUnreadCount: function () {
            if (!this.canUpdate())
                return;

            this.unreadCounter.fetch({
                data: {
                    readPosition: this.latestPosition,
                },
            }).then(_.bind(function () {
                this.fetchUnread(this.unreadCounter.get('numUnread'));
            }, this));
        },

        /**
         * Updates this.uniqueTogether with new attributes.
         *
         * @param {Object} options - should have at least one of:
         *                           type, index, or target, all of which should be strings
         * @returns {void}
         */
        updateUniqueTogether: function (options) {
            if (!options || !_.isObject(options))
                return;

            var newUniqueTogether = _.pick(options, 'type', 'index', 'target');
            this.uniqueTogether = _.extend(this.uniqueTogether, newUniqueTogether);

            // Also update the unreadCounter's uniqueTogether
            if (this.unreadCounter)
                this.updateUniqueTogether.call(this.unreadCounter, this.uniqueTogether);
        },

        fetchUnread: function (numUnread) {
            if (numUnread <= 0)
                return;

            // lazily instantiate
            this.unreadQueue = this.unreadQueue || new this.constructor(null, _.clone(this.uniqueTogether));
            this.unreadQueue.fetch({
                reset: true,
                data: {
                    limit: numUnread,
                },
            }).then(_.bind(function () {
                // adjust counter, if necessary
                var numQueued = this.unreadQueue.length;
                this.unreadCounter.set('numUnread', numQueued);
                this.trigger('unreadItemsReady', numQueued);
            }, this));
        },

        addUnread: function () {
            this.latestPosition = this.unreadQueue.latestPosition;
            this.unreadQueue.invoke('set', 'isFromUnreadQueue', true);
            this.add(this.unreadQueue.models, {
                at: 0,
            });
            this.unreadQueue.reset();
            this.unreadCounter.set('numUnread', 0);
        },

        parse: function (response, options) {
            options = options || {};

            // Store latestPosition in order to later ask the server (via getUnreadCount
            // endpoint) how many new items have been added to the feed since the last
            // request.
            if (!this.latestPosition || options.reset)
                this.latestPosition = response.response.latestPosition;

            var boundTransform = _.bind(FeedItemCollection.transform, FeedItemCollection, response.response.objects);

            response.response = _.chain(response.response.activities)
                .map(boundTransform)
                .compact()
                .value();


            if (this.uniqueTogether.type === 'home' && Boolean(response) && Boolean(response.response) && _.isArray(response.response)) {
                response.response = _.filter(response.response, function (item) {
                    return item.thread.forum.id.indexOf('channel-') === -1 || item.thread.forum.id === 'channel-discussdisqus';
                });
            }

            return parentProto.parse.call(this, response, options);
        },
    }, {
        activityFactory: new ActivityFactory(),

        /**
         * Switches the transformer used for this item based on if it's a
         * single activity item or a grouped activities item.
         */
        transform: function (objects, item) {
            return item.items ?
                this.buildFeedItemFromGroupedActivities(objects, item) :
                this.buildFeedItemFromSingleActivity(objects, item);
        },

        /**
         * Builds a FeedItem from a group of activities passed from the backend.
         * @param  {Object} objects    - The model definitions sent from the backend.
         *                               The items reference these models by type/id,
         *                               and the actual model details are contained in
         *                               this objects mapping.
         * @param  {Object} group      - The json representation of a group of activities
         * @returns {ActivityFeedItem}  - The model representation of the given item
         */
        buildFeedItemFromGroupedActivities: function (objects, group) {
            group.actors = group.actors || { items: [] };

            var activities = _.chain(group.items)
                .map(_.bind(this.buildActivity, this, objects))
                .compact()
                .value();
            var actors = _.map(group.actors.items, _.bind(activityExpander.buildUser, activityExpander, objects));
            var activity = activities[0];
            var verb = activity && activity.get('verb');

            if (!activity)
                return;

            // Occurs for anonymous reply activity. The backend returns
            // group.actors.items = []
            if (!actors.length && activity.actor) {
                group.actors.totalItems = 1;
                actors = [activity.actor];
            }

            var feedItemId = ActivityFeedItem.buildId(activities);
            var attributes = ActivityFeedItem.prototype.parse.call(null, {
                id: feedItemId,

                // Multiple replies are treated as a group of comments
                verb: verb === 'reply' && activities.length > 1 ? 'post' : verb,
                published: group.published,
                totalActors: group.actors && group.actors.totalItems || 0,
                totalActivities: group.totalItems,
                score: group.score,
            });
            var options = {
                activities: activities,
                thread: activity.thread,
            };
            // Do not use UniqueModel, because boost attributes can be changed on the
            // ActivityFeedItem, and those changes are specific to this feed.
            // Don't want those changes to affect the same activity in another feed.
            var feedItem = new ActivityFeedItem(
                attributes,
                options
            );

            feedItem.actors = actors;

            return feedItem;
        },

        /**
         * Builds a FeedItem from a single activity passed from the backend.
         * FeedItem supports multiple activities, this transformer will populate
         * the model with only the single activity.
         * @param  {Object} objects    - The model definitions sent from the backend.
         *                               The items reference these models by type/id,
         *                               and the actual model details are contained in
         *                               this objects mapping.
         * @param  {Object} item       - The json definition of one feed item.
         * @returns {ActivityFeedItem}  - The model representation of the given item
         */
        buildFeedItemFromSingleActivity: function (objects, item) {
            var activity = this.buildActivity(objects, item);

            if (!activity)
                return;

            var activities = [ activity ];
            var feedItemId = ActivityFeedItem.buildId(activities);
            var attributes = _.defaults({
                id: feedItemId,
                totalActors: 1,
                totalActivities: 1,
                verb: activity.get('verb'),
            }, item);
            var options = {
                activities: activities,
                thread: activity.thread,
            };
            // Do not use UniqueModel, because boost attributes can be changed on the
            // ActivityFeedItem, and those changes are specific to this feed.
            // Don't want those changes to affect the same activity in another feed.
            var feedItem = new ActivityFeedItem(
                attributes,
                options
            );

            feedItem.actors = activity.actor ? [activity.actor] : [];

            return feedItem;
        },

        /**
         * Builds an Activity model from the given activity item
         * and objects mapping.
         * @param  {Object} objects - Mapping of model type/id to model attributes
         * @param  {Object} item    - description of the activity, including actor, type,
         *                            published, routings, and object.
         * @returns {Activity}       - Activity model representing the activity
         */
        buildActivity: function (objects, item) {
            try {
                return this.activityFactory.createActivity(item, {
                    objects: objects,
                    parse: true,
                });
            } catch (err) {
                if (window.Raven)
                    window.Raven.captureMessage('error parsing activity item', { extra: item });
            }
        },
    });

    return FeedItemCollection;
});

